import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import {
  TEMPLATE_NAME_TAKEN_API_MESSAGE,
  TEMPLATE_NAME_TAKEN_MESSAGE,
  buildDuplicateTemplateName,
  getTemplateNameSuffix,
  isTemplateNameTaken,
  templateNamePrefix,
} from 'components/SlideOver/dataSlideOver/data/slideOverAddMission/missionTemplate/templateFormData';

/**
 * Renames the copy before creating it. The slide over that opened it watches
 * duplicatedMissionTemplateId to show the new job description.
 */
const ModalDuplicateMissionTemplate = () => {
  const { toggle } = useModal();
  const {
    pendingDuplicateMissionTemplate,
    setPendingDuplicateMissionTemplate,
    setDuplicatedMissionTemplateId,
  } = UseContext();
  const token = localStorage.getItem('xsrfToken');

  const source = pendingDuplicateMissionTemplate?.template;
  const siblings = pendingDuplicateMissionTemplate?.siblings || [];
  const [name, setName] = useState(() =>
    buildDuplicateTemplateName(source?.name, siblings)
  );
  const [isSaving, setIsSaving] = useState(false);

  // Same rule as the wizard: the qualification opens the name and cannot be
  // touched, only what follows the dash is typed.
  const namePrefix = templateNamePrefix(source?.qualification);
  const nameSuffix = getTemplateNameSuffix(name, source?.qualification);
  const onSuffixChange = (suffix) => setName(namePrefix + suffix);

  const isNameTaken = useMemo(
    () => isTemplateNameTaken(name, siblings),
    [name, siblings]
  );
  const canSubmit = !!name.trim() && !isNameTaken && !!source?._id && !isSaving;

  const handleCancel = () => {
    setPendingDuplicateMissionTemplate(null);
    toggle();
  };

  const handleConfirm = () => {
    if (!canSubmit) return;
    setIsSaving(true);
    ApiDatabase.postDuplicateAdminMissionTemplate(
      { token, id: source._id, data: { name: name.trim() } },
      (created) => {
        setIsSaving(false);
        setPendingDuplicateMissionTemplate(null);
        toast.success('La fiche de poste a bien été dupliquée.');
        setDuplicatedMissionTemplateId(created?._id || null);
        toggle();
      },
      (err) => {
        setIsSaving(false);
        console.error(err);
        const apiMessage = err?.response?.data?.message;
        toast.error(
          apiMessage === TEMPLATE_NAME_TAKEN_API_MESSAGE
            ? TEMPLATE_NAME_TAKEN_MESSAGE
            : 'Duplication impossible'
        );
      }
    );
  };

  return (
    <>
      <Modalheader hide={handleCancel} hideShow={true}>
        Dupliquer la fiche de poste
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-700">
            La copie reprend tout le contenu de la fiche. Donnez-lui un nom.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-700">
              Nom de la nouvelle fiche
            </span>
            <div
              className={`flex w-full min-h-10 rounded-lg border overflow-hidden ${
                isNameTaken ? 'border-red-500' : 'border-gray-200'
              }`}
            >
              {namePrefix ? (
                <span
                  className="shrink-0 max-w-[55%] px-3 py-2 text-sm leading-5 text-gray-800 bg-gray-100 border-r border-gray-200 truncate"
                  title={namePrefix}
                >
                  {namePrefix}
                </span>
              ) : null}
              <input
                type="text"
                autoFocus
                value={nameSuffix}
                onChange={(event) => onSuffixChange(event.target.value)}
                placeholder="Complément"
                className="flex-1 min-w-0 h-10 px-3 text-sm placeholder-gray-600 border-0 focus:outline-none focus:ring-0"
              />
            </div>
          </label>
          {isNameTaken ? (
            <span className="text-sm text-red-500">
              {TEMPLATE_NAME_TAKEN_MESSAGE}.
            </span>
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={handleCancel}>
          Annuler
        </Button>
        <Button
          size="LG"
          color={canSubmit ? 'PRIMARY' : 'DISABLED'}
          onClick={handleConfirm}
          disabled={!canSubmit}
        >
          {isSaving ? 'Duplication…' : 'Dupliquer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalDuplicateMissionTemplate;
